import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import useVoucher from '../../hooks/useVoucher';
import { getStatusLabel, resolvePaymentMethodLabel } from '../../util/paymentUtils';
import PaymentDetails from '../components/Payment/PaymentDetails';

function VoucherHistory({ open, onClose, voucher, loading }) {
  const theme = useTheme();
  const { getPaymentById } = useVoucher();
  const [paymentDetails, setPaymentDetails] = useState({});
  const [loadingPayments, setLoadingPayments] = useState({});

  const txs = Array.isArray(voucher?.transactionHistory) ? voucher.transactionHistory : [];

  // Load payment details for transactions that have relatedPaymentId
  useEffect(() => {
    if (voucher && txs.length > 0) {
      txs.forEach(async tx => {
        if (
          tx.relatedPaymentId &&
          !paymentDetails[tx.relatedPaymentId] &&
          !loadingPayments[tx.relatedPaymentId]
        ) {
          setLoadingPayments(prev => ({ ...prev, [tx.relatedPaymentId]: true }));
          try {
            const payment = await getPaymentById(tx.relatedPaymentId);
            setPaymentDetails(prev => ({ ...prev, [tx.relatedPaymentId]: payment }));
          } catch (error) {
            console.error('Error loading payment details:', error);
            setPaymentDetails(prev => ({ ...prev, [tx.relatedPaymentId]: null }));
          } finally {
            setLoadingPayments(prev => ({ ...prev, [tx.relatedPaymentId]: false }));
          }
        }
      });
    }
  }, [voucher, txs, paymentDetails, loadingPayments, getPaymentById]);

  if (!voucher && !loading) return null;

  // Extract payment method from webhookEvents if not available in paymentMethod field
  const getPaymentMethod = resolvePaymentMethodLabel;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' flexDirection='column'>
          <Typography variant='h6'>Gutschein: {voucher?.voucherCode || ''}</Typography>
          <Typography variant='subtitle2'>
            Status: {getStatusLabel(voucher?.status) || ''}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
            <CircularProgress />
          </Box>
        ) : !txs || txs.length === 0 ? (
          <Typography color='text.secondary' sx={{ my: 2 }}>
            Keine Transaktionen vorhanden
          </Typography>
        ) : (
          <Box>
            {txs.map(tx => (
              <Paper key={tx._id || Math.random()} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle2'>
                    {tx.type === 'load'
                      ? 'Aufladung'
                      : tx.type === 'redeem'
                        ? 'Einlösung'
                        : tx.type}{' '}
                    vom {tx.date ? dayjs(tx.date).format('DD.MM.YYYY HH:mm') : ''}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {tx.branchLongName != null && tx.branchLongName !== '' && (
                      <Typography variant='body2'>
                        <strong>Filiale:</strong> {tx.branchLongName}
                      </Typography>
                    )}
                    {tx.issuedByUsername != null && tx.issuedByUsername !== '' && (
                      <Typography variant='body2'>
                        <strong>Benutzer:</strong> {tx.issuedByUsername}
                      </Typography>
                    )}
                    <Typography variant='body2'>
                      <strong>Betrag:</strong>{' '}
                      {tx.amount?.sum != null
                        ? tx.amount.sum.toFixed(2).replace('.', ',') + ' €'
                        : ''}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>Restguthaben nach Transaktion:</strong>{' '}
                      {tx.balanceAfterTransaction?.sum != null
                        ? tx.balanceAfterTransaction.sum.toFixed(2).replace('.', ',') + ' €'
                        : ''}
                    </Typography>
                    {tx.comment && (
                      <Typography variant='body2'>
                        <strong>Kommentar:</strong> {tx.comment}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {tx.relatedPaymentId && (
                  <Box sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.divider}` }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Zahlungsdetails
                    </Typography>
                    <PaymentDetails
                      payment={paymentDetails[tx.relatedPaymentId]}
                      loading={loadingPayments[tx.relatedPaymentId]}
                      getPaymentMethod={getPaymentMethod}
                    />
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

export default VoucherHistory;
